/**
 * What an Elliott count implies for the bars that have not happened yet.
 *
 * Three things live here, all derived from the pivots the engine already returns:
 *
 *   1. {@link degreeLayers}     which counts to draw together, so major and minor read as a hierarchy
 *   2. {@link fibTargets}       the standard ratio projections for the wave in progress
 *   3. {@link futureProjection} the dashed path from the last pivot towards the nearest target
 *
 * <h2>Why the targets are computed here and not in the engine</h2>
 * The backend returns a single scalar, {@code primaryTargetPrice}, from its bias summary. There is
 * no per-wave projection on the wire and {@code Fib.java} is a <em>scoring</em> helper — it grades
 * how close an observed ratio came to a guideline, which is the opposite direction of travel from
 * projecting one. The pivots needed to project are already here, and this is pure geometry over
 * them, so it is computed and tested in one place rather than added to the analysis round trip.
 *
 * <h2>X is a bar index</h2>
 * Same rule as the drawing tools: the chart's x-axis is a category axis of date strings, so every
 * index here refers to the caller's own bar list and is resolved to a date by the renderer.
 */

import { DEGREE_STYLES, styleForDegree } from './ElliottDegreeStyles';

/**
 * Ratios projected for each wave, nearest first.
 *
 * The textbook set, and deliberately no more than that. Every one of these is measured from the
 * end of the preceding wave, in the direction the pattern's FIRST wave travelled.
 *
 *   wave 3 — 1.618 and 2.618 of wave 1, the two extension targets in common use
 *   wave 5 — 0.618 and 1.0 of wave 1; the second is the equality guideline, where wave 5 matches
 *            wave 1, which is what an unextended fifth usually does
 *   wave C — 1.0 and 1.618 of wave A; equality first, because a zigzag's C most often equals A
 */
export const TARGET_RATIOS = {
  '3': [1.618, 2.618],
  '5': [0.618, 1.0],
  C: [1.0, 1.618],
  // A contracting triangle's legs shrink: each is about 0.618 of the one before, and 0.5 is the
  // secondary reading. These are the only ratios here measured against the IMMEDIATELY preceding
  // leg rather than against the pattern's first wave - see MEASURED_AGAINST_TRIANGLE.
  D: [0.618, 0.5],
  E: [0.618, 0.5],
};

/**
 * Which wave a count is working on, keyed by the last pivot it has labelled.
 *
 * Read from the label rather than counted from the list length, because a count can begin at a
 * pivot the engine did not label (its origin) and because a corrective count's third leg is 'C',
 * not '3'. Anything not in this map — a count sitting on wave 1, 3 or A, or a finished one — gets
 * no target, which is the honest answer: there is no standard projection for the wave you are
 * already inside.
 */
const NEXT_WAVE_AFTER = { 2: '3', 4: '5', B: 'C' };

/**
 * The same question for a triangle, where the label alone cannot answer it.
 *
 * <p>'C' is TERMINAL in a zigzag or a flat and mid-pattern in a triangle, so one map keyed on the
 * label is wrong for one of them. The engine labels a triangle A-B-C-D-E and
 * {@code TriangleEvaluator.minimumWaves()} is 4 — so a triangle returned with four legs is one in
 * PROGRESS, waiting on E, and it is the commonest count on a long series.
 *
 * <p>Without this a triangle sitting at D produced no projection at all: the count stopped dead at
 * W(D) with nothing drawn after it, which is what "a gap with no connecting lines after Wave D"
 * was. Wave E was never missing from the count — it had not happened yet, and nothing was
 * projecting it.
 */
const NEXT_WAVE_AFTER_TRIANGLE = { B: 'C', C: 'D', D: 'E' };

/** The wave whose length the projection is measured in, for each projected wave. */
const MEASURED_AGAINST = { 3: '1', 5: '1', C: 'A' };

/** In a triangle each leg is measured against the one immediately before it, not against wave A. */
const MEASURED_AGAINST_TRIANGLE = { C: 'B', D: 'C', E: 'D' };

/**
 * Which way the projected wave travels relative to the leg it is measured on.
 *
 * <p>+1 everywhere except inside a triangle. Wave 3 runs the same way as wave 1, wave 5 the same
 * way as wave 1, wave C the same way as wave A — so projecting them is base + leg x ratio. A
 * triangle alternates: E travels OPPOSITE to D, and D opposite to C. Using +1 there draws every
 * triangle projection backwards, off the wrong side of the pattern, and it looks entirely plausible
 * because the price is the right distance away.
 */
const PROJECTION_SIGN_TRIANGLE = -1;

/** Patterns whose legs alternate direction and are measured on the preceding leg. */
const TRIANGLE_PATTERNS = new Set(['TRIANGLE', 'CONTRACTING_TRIANGLE', 'EXPANDING_TRIANGLE',
  'BARRIER_TRIANGLE', 'RUNNING_TRIANGLE']);

/** Whether a pattern name is a triangle, normalised the way describeWave normalises. */
function isTriangle(pattern) {
  return TRIANGLE_PATTERNS.has(String(pattern ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_'));
}

const isNum = v => typeof v === 'number' && Number.isFinite(v);

/** A pivot usable as geometry: placed on a bar, at a price. */
const usablePivot = p =>
  !!p && Number.isInteger(p.barIndex) && isNum(p.price);

/** Ladder order, coarsest first — the insertion order of DEGREE_STYLES is that order. */
const LADDER = Object.keys(DEGREE_STYLES);

/**
 * How coarse a degree is: 0 is the boldest tier, larger is finer.
 *
 * Resolved through {@link styleForDegree} so an engine degree (MACRO, MICRO) and a ladder tier
 * (PRIMARY, MINUTE) rank on the same scale — the caller should not have to know which vocabulary
 * its data speaks, and the two are mixed on the wire.
 */
export function degreeRank(degree) {
  const style = styleForDegree(degree);
  const index = LADDER.findIndex(tier => DEGREE_STYLES[tier] === style);
  return index < 0 ? LADDER.length : index;
}

/**
 * How stale a count may be at the anchor, as a multiple of its OWN median leg.
 *
 * <p>The engine's own currency test is {@code maxPivotsBehind = 2}, counted in PIVOTS — and a pivot
 * at MACRO degree is worth about a thousand bars. Measured on SPY at 41 anchors that let a count
 * which finished 2,445 bars (roughly ten years) before the clicked bar be drawn beside one covering
 * the last fortnight, with nothing in between. That is the "huge gap": not a break in a polyline,
 * which {@code connectNulls} already prevents, but two counts from different eras on one chart.
 *
 * <p>Three is where the distribution has its knee. Staleness in leg-lengths runs p50 0.77, p75 2.60,
 * p90 6.40, max 73 — a body of current counts and a thin tail of ancient ones. A cut at 3 drops 18%
 * of layers and empties exactly one anchor of 41; at 2 it drops 31% and starts taking live counts
 * with it.
 */
export const MAX_STALENESS_LEGS = 3;

/** A count is never called stale for being less than this far behind, however short its legs. */
export const MIN_CURRENCY_BARS = 20;

/** The median gap between consecutive pivots, in bars — the count's own natural leg length. */
export function medianLegBars(points) {
  const usable = (points || []).filter(usablePivot);
  if (usable.length < 2) return 0;
  const legs = [];
  for (let i = 1; i < usable.length; i++) legs.push(usable[i].barIndex - usable[i - 1].barIndex);
  legs.sort((a, b) => a - b);
  const mid = Math.floor(legs.length / 2);
  return legs.length % 2 ? legs[mid] : Math.round((legs[mid - 1] + legs[mid]) / 2);
}

/** Bars from a count's last pivot to the anchor. */
export function barsBehind(layer, anchorIndex) {
  const points = (layer?.points || []).filter(usablePivot);
  if (points.length === 0 || !Number.isInteger(anchorIndex)) return 0;
  return anchorIndex - points[points.length - 1].barIndex;
}

/** Whether a count still describes the market at the anchor, judged on its own timescale. */
export function isCurrentAtAnchor(layer, anchorIndex) {
  if (!Number.isInteger(anchorIndex)) return true;   // no anchor, no staleness to measure
  const limit = Math.max(MIN_CURRENCY_BARS, medianLegBars(layer.points) * MAX_STALENESS_LEGS);
  return barsBehind(layer, anchorIndex) <= limit;
}

/**
 * The layers still current at the anchor.
 *
 * <p>Never empty when it was given something: if every count is stale the least stale one survives,
 * carrying {@code stale: true}. Returning nothing would leave a blank chart and no explanation,
 * which reads as the study being broken rather than as the honest "nothing here is recent".
 */
export function currentLayers(layers, anchorIndex) {
  const all = Array.isArray(layers) ? layers : [];
  if (all.length === 0 || !Number.isInteger(anchorIndex)) return all;

  const kept = all.filter(l => isCurrentAtAnchor(l, anchorIndex));
  if (kept.length > 0) return kept;

  const leastStale = [...all].sort((a, b) => barsBehind(a, anchorIndex) - barsBehind(b, anchorIndex))[0];
  return [{ ...leastStale, stale: true, barsBehind: barsBehind(leastStale, anchorIndex) }];
}

/**
 * The counts worth drawing at once, coarsest degree first.
 *
 * <h2>One per degree, not the top three</h2>
 * {@code top3WaveCounts} is a ranked list of <em>alternative readings of the same move</em>. Drawing
 * all three would put three competing polylines on the chart, which is not a hierarchy — it is
 * three answers to one question, and a reader cannot tell which pivots belong together.
 *
 * A hierarchy is what you get when the counts sit at DIFFERENT degrees: a coarse structure, and a
 * finer one inside it. So the counts are grouped by degree and only the most confident of each is
 * kept. If the engine only found counts at one degree, one layer comes back and the chart says so
 * by drawing one — which is correct, rather than inventing a sub-count to fill the hierarchy.
 */
export function degreeLayers(result, anchorIndex) {
  const counts = Array.isArray(result?.top3WaveCounts) ? result.top3WaveCounts : [];
  const primary = result?.primaryConclusion;
  // The primary is included even if it is somehow absent from top3, so the layer set can never be
  // missing the count the rest of the dialog is describing.
  const all = primary ? [primary, ...counts.filter(c => c !== primary)] : counts;

  const bestByDegree = new Map();
  all.forEach(count => {
    const points = count?.waveOverlay?.points;
    if (!Array.isArray(points)) return;
    const usable = points.filter(usablePivot);
    if (usable.length < 2) return;

    const degree = count.waveOverlay.degree || count.degree;
    const key = String(degree || '').toUpperCase();
    const confidence = isNum(count.confidencePercent) ? count.confidencePercent : -1;
    const existing = bestByDegree.get(key);
    if (!existing || confidence > existing.confidence) {
      bestByDegree.set(key, {
        degree,
        confidence,
        points: usable,
        pattern: count.pattern,
        position: count.position,
        complete: !!count.complete,
      });
    }
  });

  // Stale counts are dropped BEFORE the per-degree pick would otherwise be made moot: a count
  // that stopped describing the market a decade before the clicked bar is not the best reading of
  // its degree, it is a reading of a different era.
  const current = currentLayers([...bestByDegree.values()], anchorIndex);
  return current.sort((a, b) => degreeRank(a.degree) - degreeRank(b.degree));
}

/** The pivot that terminates a labelled wave, or null. */
function pivotLabelled(points, label) {
  return points.find(p => String(p.label ?? '').trim().toUpperCase() === label) || null;
}

/**
 * The signed length of a labelled wave, or null when either end is missing.
 *
 * Signed on purpose: the sign is the direction the pattern travels, and every projection below is
 * measured in it. Taking an absolute value and re-deriving the direction from "is the count up"
 * gets a corrective pattern backwards — a downward zigzag caught in wave B has travelled net
 * upward, which is exactly the case {@code WaveCount.directionUp} exists to distinguish.
 */
function signedLeg(points, label) {
  const end = pivotLabelled(points, label);
  if (!end) return null;
  const endIndex = points.indexOf(end);
  if (endIndex < 1) return null;
  const start = points[endIndex - 1];
  if (!usablePivot(start)) return null;
  return end.price - start.price;
}

/**
 * Horizontal price targets for the wave a count is currently working on.
 *
 * Returns `[]` — not a guess — when the count is not sitting at one of the three positions with a
 * standard projection. That is most of the time, and saying nothing is the right output: a target
 * line drawn from a rule that does not apply is indistinguishable on screen from one that does.
 *
 * @returns {{wave:string, ratio:number, price:number, fromIndex:number, label:string}[]}
 */
export function fibTargets(layer) {
  const points = Array.isArray(layer?.points) ? layer.points.filter(usablePivot) : [];
  if (points.length < 2) return [];

  const triangle = isTriangle(layer?.pattern);
  const lastLabel = String(points[points.length - 1].label ?? '').trim().toUpperCase();
  const wave = (triangle ? NEXT_WAVE_AFTER_TRIANGLE : NEXT_WAVE_AFTER)[lastLabel];
  if (!wave) return [];

  const base = points[points.length - 1];
  const measuredAgainst = (triangle ? MEASURED_AGAINST_TRIANGLE : MEASURED_AGAINST)[wave];
  const leg = signedLeg(points, measuredAgainst);
  if (leg === null || leg === 0) return [];

  const sign = triangle ? PROJECTION_SIGN_TRIANGLE : 1;

  return (TARGET_RATIOS[wave] || []).map((ratio) => {
    const price = base.price + sign * leg * ratio;
    return {
      wave,
      ratio,
      price,
      fromIndex: base.barIndex,
      measuredAgainst,
      // "W3 1.618 (152.40)" — the wave, the ratio it came from, and the price. All three, because a
      // bare price on a chart says nothing about why it is drawn there.
      label: `W${wave} ${ratio} (${price.toFixed(2)})`,
    };
  });
}

/**
 * The dashed path from the last pivot towards the nearest target.
 *
 * One segment, not a full five-wave sketch. The engine has established where the count currently
 * is; anything past the wave in progress would be drawing a shape nobody has evidence for, and a
 * projected wave 4 and 5 rendered in the same pen as a projected wave 3 would claim they are
 * equally supported.
 *
 * @param horizonIndex the bar the projection runs out to — the caller's forward budget
 * @returns {{from:object, to:object, wave:string, label:string}|null}
 */
export function futureProjection(layer, horizonIndex) {
  const targets = fibTargets(layer);
  if (targets.length === 0) return null;
  if (!Number.isInteger(horizonIndex)) return null;

  const nearest = targets[0];
  // Never backwards. A count whose last pivot already sits past the horizon has nothing to project
  // into — drawing it would produce a segment running right to left out of the pivot.
  if (horizonIndex <= nearest.fromIndex) return null;

  const points = layer.points.filter(usablePivot);
  const base = points[points.length - 1];

  return {
    from: { index: base.barIndex, price: base.price },
    to: { index: horizonIndex, price: nearest.price },
    wave: nearest.wave,
    label: `W${nearest.wave} →`,
  };
}

/**
 * Motive patterns advance the trend; corrective ones retrace it.
 *
 * Keyed on the pattern name the engine sends, normalised, because the two families answer different
 * questions about the same leg: a "3" inside an impulse is the extension wave, while a "3" is not a
 * label a corrective pattern uses at all.
 */
const MOTIVE_PATTERNS = new Set(['IMPULSE', 'DIAGONAL', 'LEADING_DIAGONAL', 'ENDING_DIAGONAL']);

/**
 * The guideline attached to each wave position, as a chartist would state it.
 *
 * <p>These are <b>guidelines, not the rules</b>, and the distinction is the engine's own: every
 * count it returns has already passed the strict rules, which are gates rather than scores. So none
 * of these can be quoted as "this count is legal because…" — they describe what a typical wave does,
 * which is what a reader wants when hovering a leg.
 *
 * <p>Wave 1 and wave A carry none. There is nothing prior to measure them against, and inventing a
 * ratio for them would be the one line here that is not standard.
 */
export const WAVE_GUIDELINES = {
  1: [],
  2: ['Retraces 50–61.8% of wave 1', 'Never retraces more than 100% of wave 1'],
  3: ['Target = 1.618 × wave 1, projected from the end of wave 2', 'Never the shortest motive wave'],
  4: ['Retraces ≤ 38.2% of wave 3', 'Does not overlap wave 1 in an impulse'],
  5: ['Often equals wave 1, or 0.618 × wave 1 from the end of wave 4'],
  A: [],
  B: ['Retraces 38.2–78.6% of wave A'],
  C: ['Often equals wave A, or 1.618 × wave A from the end of wave B'],
  D: ['In a contracting triangle, about 0.618 of wave C', 'Travels against wave C'],
  E: ['In a contracting triangle, about 0.618 of wave D', 'Usually the last leg — the triangle then resolves'],
};

/**
 * What a wave leg is, in words: its degree, its family, and the guidelines that apply to it.
 *
 * <p>Returned as data rather than as a formatted string so the caller decides the medium — the
 * chart wants HTML in a tooltip, and a test wants to assert the parts without parsing markup.
 *
 * @returns {{label:string, degreeName:string, kind:string, guidelines:string[]}|null}
 */
export function describeWave(label, degree, pattern) {
  const raw = String(label ?? '').trim().toUpperCase();
  if (!raw) return null;

  const patternKey = String(pattern ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_');
  // The LEG decides, with the pattern as the tiebreak. A numbered leg is motive and a lettered one
  // is corrective wherever it appears — including waves A and C inside a zigzag, which are
  // themselves motive structures within a corrective one. Reading the family off the pattern alone
  // would call every leg of a zigzag corrective, which is what the notation exists to distinguish.
  let kind;
  if (/^[1-5]$/.test(raw)) kind = 'Motive';
  // D and E are triangle legs and appear nowhere else, so they are corrective on the label alone.
  else if (/^[A-EWXYZ]$/.test(raw)) kind = 'Corrective';
  else kind = MOTIVE_PATTERNS.has(patternKey) ? 'Motive' : 'Corrective';

  return {
    label: raw,
    degreeName: styleForDegree(degree).name,
    kind,
    guidelines: WAVE_GUIDELINES[raw] || [],
  };
}

/**
 * The ratio a completed wave actually achieved against the wave it is conventionally measured on.
 *
 * <p>Wave 3 and 5 against wave 1, wave C against wave A — the same pairings {@link TARGET_RATIOS}
 * projects with, so an annotation and a target on the same chart are talking about the same
 * measurement. Null when either leg is missing or the reference has no length, because a ratio
 * against nothing is not a number worth printing.
 */
export function achievedRatio(points, label) {
  const against = MEASURED_AGAINST[label];
  if (!against) return null;
  const leg = signedLeg(points, label);
  const reference = signedLeg(points, against);
  if (leg === null || reference === null || reference === 0) return null;
  return leg / reference;
}

/**
 * One annotation per drawn wave leg, placed on the leg rather than at its ends.
 *
 * <h2>Why the midpoint</h2>
 * The pivot chips already name the wave in its degree's notation — `[3]`, `(3)`, `iii`. What they
 * cannot carry is what that notation MEANS, which until now lived only in a hover tooltip and in the
 * dialog. Putting it at the midpoint attaches it to the leg it describes rather than to a point two
 * legs share, and leaves the pivot chips uncluttered.
 *
 * <h2>Which side</h2>
 * Above a rising leg, below a falling one. Price between two pivots generally travels on the inside
 * of the straight line joining them — that is what makes the line a chord rather than a path — so
 * the outside is the side with room. It is a convention rather than a guarantee, which is why the
 * renderer also offsets by a fixed pixel distance instead of trusting the price scale.
 *
 * @returns {{index:number, price:number, text:string, up:boolean, label:string}[]}
 */
export function waveSegments(layer) {
  const points = Array.isArray(layer?.points) ? layer.points.filter(usablePivot) : [];
  if (points.length < 2) return [];

  const out = [];
  for (let i = 1; i < points.length; i++) {
    const from = points[i - 1];
    const to = points[i];
    const described = describeWave(to.label, layer.degree, layer.pattern);
    if (!described) continue;   // the count's origin terminates nothing

    // Rounded towards the leg's own end, so a two-bar leg annotates on a bar rather than between
    // two. A fractional index cannot address a category axis.
    const index = Math.round((from.barIndex + to.barIndex) / 2);
    const price = (from.price + to.price) / 2;

    const ratio = achievedRatio(points, described.label);
    const parts = [described.degreeName];
    if (ratio !== null) {
      // Magnitude only. The sign is the direction, which the leg itself already shows, and a
      // "-1.72x" on a falling wave 3 reads as a negative extension rather than a downward one.
      parts.push(`${Math.abs(ratio).toFixed(2)}×`);
    } else if (described.kind === 'Corrective') {
      parts.push('Corrective');
    }

    out.push({
      index,
      price,
      // "W3 (Primary, 1.72×)" — the leg, its degree, and what it did. Bracketed so the leg reads
      // first: on a busy chart the wave number is what a reader scans for.
      text: `W${/^[0-9]$/.test(described.label) ? described.label : `(${described.label})`}`
        + ` (${parts.join(', ')})`,
      up: to.price >= from.price,
      label: described.label,
      // Everything a hover should say about this leg. Carried on the segment because the renderer's
      // formatter runs long after the layer has gone out of scope, and because a test can assert
      // these fields without rendering anything.
      kind: described.kind,
      degreeName: described.degreeName,
      guidelines: described.guidelines,
      fromIndex: from.barIndex,
      toIndex: to.barIndex,
      bars: to.barIndex - from.barIndex,
      priceMove: to.price - from.price,
      pricePercent: from.price !== 0 ? (to.price - from.price) / Math.abs(from.price) : null,
      ratio,
    });
  }
  return out;
}

/**
 * A target's callout text: what it is, which ratio produced it, and the price.
 *
 * <p>"Equality" rather than "1×" where the ratio is one, because that is what the guideline is
 * called — a fifth wave equal to the first, or a C equal to its A — and a reader looking for the
 * equality target should not have to translate.
 */
export function targetCallout(target) {
  if (!target || !isNum(target.price)) return '';
  const name = target.ratio === 1 ? 'Equality Target' : `Target ${target.ratio}×`;
  return `W${target.wave} ${name}: ${target.price.toFixed(2)}`;
}

/**
 * Every count the engine returned that carries usable geometry, most confident first.
 *
 * <p>Distinct from {@link degreeLayers}, which keeps one count per degree because DRAWING several
 * counts of the same degree puts rival polylines on one chart. Reading them is a different job: a
 * count that was not drawn still made a projection, and a projection nobody lists is one nobody can
 * check. At SPY bar 4001 the two MACRO counts tie at 80% confidence, the drawn one has no target
 * and the dropped one has two — so keeping only the drawn layers would report "no projections" on
 * a bar that made two.
 */
export function countLayers(result, anchorIndex) {
  const counts = Array.isArray(result?.top3WaveCounts) ? result.top3WaveCounts : [];
  const primary = result?.primaryConclusion;
  const all = primary ? [primary, ...counts.filter(c => c !== primary)] : counts;

  const out = [];
  const seen = new Set();
  all.forEach((count) => {
    const points = (count?.waveOverlay?.points || []).filter(usablePivot);
    if (points.length < 2) return;
    // Two entries of top3 can describe the same labelling; the identity is the pivots, not the
    // object, because the primary is also re-listed inside top3 by value.
    const key = points.map(p => `${p.barIndex}:${p.price}:${p.label ?? ''}`).join('|');
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      degree: count.waveOverlay.degree || count.degree,
      pattern: count.pattern,
      position: count.position,
      complete: !!count.complete,
      confidence: isNum(count.confidencePercent) ? count.confidencePercent : null,
      points,
    });
  });
  return currentLayers(out, anchorIndex).sort((a, b) => (b.confidence ?? -1) - (a.confidence ?? -1));
}

/** The bar span and price travel of a labelled leg, or null. */
function legMetrics(points, label) {
  const end = pivotLabelled(points, label);
  if (!end) return null;
  const endIndex = points.indexOf(end);
  if (endIndex < 1) return null;
  const start = points[endIndex - 1];
  if (!usablePivot(start)) return null;
  return {
    label,
    fromIndex: start.barIndex,
    toIndex: end.barIndex,
    bars: end.barIndex - start.barIndex,
    price: end.price - start.price,
  };
}

/**
 * Every projection standing at the anchor, across every count — with what produced it.
 *
 * <p>The extra fields over {@link fibTargets} are what make a projection checkable rather than
 * merely drawable: which leg it was measured against and how long that leg was in price and in
 * bars, which pivot it is measured from, and which way it points. The bar length is not decoration
 * — it sets the horizon a projection is judged over, since a target has to be reached in something
 * like the time the measured wave took to mean anything.
 *
 * @returns {Array<object>}
 */
export function allProjections(result, anchorIndex) {
  const out = [];
  const seen = new Set();

  countLayers(result, anchorIndex).forEach((layer) => {
    const points = layer.points;
    const base = points[points.length - 1];
    const targets = fibTargets(layer);
    if (targets.length === 0) return;

    // Off the target itself, not re-derived: a triangle measures on the preceding leg and
    // everything else on the pattern's first wave, and re-deriving here would disagree with the
    // price fibTargets already computed.
    const measuredWave = targets[0].measuredAgainst || MEASURED_AGAINST[targets[0].wave];
    const leg = legMetrics(points, measuredWave);
    if (!leg || leg.price === 0) return;

    targets.forEach((t) => {
      // Two counts at different degrees can land on the same price from the same pivot. That is one
      // projection seen twice, and listing it twice would inflate every count in the summary.
      const key = `${t.fromIndex}|${t.wave}|${t.ratio}|${t.price.toFixed(4)}`;
      if (seen.has(key)) return;
      seen.add(key);

      out.push({
        ...t,
        pattern: layer.pattern,
        degree: layer.degree,
        confidence: layer.confidence,
        position: layer.position,
        baseIndex: base.barIndex,
        basePrice: base.price,
        measuredWave,
        legPrice: leg.price,
        legBars: leg.bars,
        // Which way the projection points, read from the TARGET against the base rather than from
        // the measured leg. They used to agree by construction; a triangle breaks that, because its
        // next leg travels opposite to the one it is measured on. Taking the leg's sign there marks
        // an upward projection as downward, and verification then watches the wrong side of every
        // bar — a miss reported as a hit.
        direction: t.price >= base.price ? 1 : -1,
        // How far price has to travel from the pivot the projection starts at.
        distance: t.price - base.price,
      });
    });
  });

  // Nearest first: the projection price has to be reached before the further one, so this is also
  // the order they can resolve in.
  return out.sort((a, b) => Math.abs(a.distance) - Math.abs(b.distance));
}

/**
 * The complete labelled structure of every count, leg by leg, up to the anchor.
 *
 * <p>What the chart draws is a polyline; what a reader wants to check is the measurements behind
 * it. Each leg gets its label, its family, the bars it spans in dates the reader recognises, how
 * far it travelled in price and in bars, and — where the guideline defines one — the ratio it
 * actually achieved against the wave it is conventionally measured on.
 *
 * <p>Built from {@link countLayers} rather than {@link degreeLayers} for the same reason the
 * projections are: a count that lost the per-degree tie still describes real structure, and it is
 * the one whose projection may be listed below.
 *
 * @param {object} result the analysis response
 * @param {Array<string>} dates chart dates, indexed as the engine indexes bars
 */
export function waveStructure(result, dates, anchorIndex) {
  const dateAt = (i) => (Array.isArray(dates) && dates[i] ? String(dates[i]).slice(0, 10) : null);

  return countLayers(result, anchorIndex).map((layer) => {
    const points = layer.points;
    const legs = [];
    for (let i = 1; i < points.length; i++) {
      const from = points[i - 1];
      const to = points[i];
      const described = describeWave(to.label, layer.degree, layer.pattern);
      if (!described) continue;   // the count's origin terminates no leg
      legs.push({
        label: described.label,
        kind: described.kind,
        fromIndex: from.barIndex,
        toIndex: to.barIndex,
        fromDate: dateAt(from.barIndex),
        toDate: dateAt(to.barIndex),
        fromPrice: from.price,
        toPrice: to.price,
        bars: to.barIndex - from.barIndex,
        // Signed, because the direction is half of what a leg is. The renderer takes the magnitude
        // where it wants one; discarding the sign here would make a corrective leg unreadable.
        pricePrice: to.price - from.price,
        pricePercent: from.price !== 0 ? (to.price - from.price) / Math.abs(from.price) : null,
        ratio: achievedRatio(points, described.label),
        guidelines: described.guidelines,
      });
    }

    const first = points[0];
    const last = points[points.length - 1];
    return {
      degree: layer.degree,
      degreeName: styleForDegree(layer.degree).name,
      pattern: layer.pattern,
      position: layer.position,
      complete: layer.complete,
      confidence: layer.confidence,
      fromIndex: first.barIndex,
      toIndex: last.barIndex,
      fromDate: dateAt(first.barIndex),
      toDate: dateAt(last.barIndex),
      spanBars: last.barIndex - first.barIndex + 1,
      legs,
    };
  }).filter(c => c.legs.length > 0);
}

/**
 * The single pen every forward projection is drawn in.
 *
 * <p>Gold, and deliberately not the degree's colour and not the outcome's. The chart's structural
 * lines are the degree ladder's blues and greys; a reader glancing at it has to be able to say
 * "that part is what happened, that part is what is claimed" before reading a single label, and one
 * unmistakable colour for every projection is what does that.
 *
 * <p>The outcome has NOT been thrown away — it moves to the end badge and the hit pin, which are
 * the things a reader looks AT rather than glances past. Colouring the lines by verdict made four
 * different-coloured dashed lines that each had to be decoded before you knew they were even
 * projections.
 */
export const PROJECTION_COLOR = '#f9a825';

/** The pen for a projection that has already been settled by later price action. */
export const PROJECTION_RESOLVED_COLOR = '#8d6e00';

/**
 * The unlabelled stretches between one count's last pivot and the next count's first.
 *
 * <h2>Why these are not waves</h2>
 * The counts are independent readings at different resolutions, not a parent and its child — the
 * engine ranks each degree separately. So a Primary count ending at W(D) and an Intermediate count
 * beginning at W1 forty bars later do not share a leg, and drawing a solid line between them in the
 * degree's own pen would assert a wave neither count labelled.
 *
 * <p>They ARE, however, the same price series, and a break in the drawing reads as the study having
 * dropped something. So the stretch is bridged in a thin dotted line at low opacity, carrying no
 * label and no pivot dot: enough for the eye to follow the sequence across the transition, not
 * enough to claim anything about it.
 *
 * @param {Array<object>} layers drawn layers, any order
 * @returns {Array<{fromIndex:number, fromPrice:number, toIndex:number, toPrice:number,
 *          fromDegree:string, toDegree:string, bars:number}>}
 */
export function degreeBridges(layers) {
  const usable = (Array.isArray(layers) ? layers : [])
    .map(l => ({ degree: l.degree, points: (l.points || []).filter(usablePivot) }))
    .filter(l => l.points.length >= 2)
    // Chained in BAR order, not in degree order. The drawn set is sorted coarsest-first so the
    // hierarchy reads correctly, and that is unrelated to where each count sits on the chart.
    .sort((a, b) => a.points[0].barIndex - b.points[0].barIndex);

  const out = [];
  for (let i = 1; i < usable.length; i++) {
    const prev = usable[i - 1].points[usable[i - 1].points.length - 1];
    const next = usable[i].points[0];
    // Only where there is genuinely a hole. Counts at different degrees frequently overlap or
    // interleave, and a "bridge" running backwards through a stretch both counts already cover
    // would be a line drawn over itself.
    if (next.barIndex <= prev.barIndex) continue;
    out.push({
      fromIndex: prev.barIndex,
      fromPrice: prev.price,
      toIndex: next.barIndex,
      toPrice: next.price,
      fromDegree: usable[i - 1].degree,
      toDegree: usable[i].degree,
      bars: next.barIndex - prev.barIndex,
    });
  }
  return out;
}

/**
 * Colour per outcome, used on the end badge and the hit pin rather than on the line.
 */
export const VERDICT_COLORS = {
  REACHED: '#2e7d32',
  INVALIDATED: '#c62828',
  EXPIRED: '#ef6c00',
  OPEN: '#1565c0',
  UNTESTABLE: '#78909c',
};

/** How the outcome reads on the chart label. */
const VERDICT_TEXT = {
  REACHED: v => `reached in ${v.barsToResolve} bars`,
  INVALIDATED: v => (Number.isFinite(v.bestFraction)
    ? `count broke - got ${Math.round(v.bestFraction * 100)}%` : 'count broke'),
  EXPIRED: v => (Number.isFinite(v.bestFraction)
    ? `out of time - got ${Math.round(v.bestFraction * 100)}%` : 'ran out of time'),
  OPEN: () => 'still open',
  UNTESTABLE: () => 'not yet testable',
};

/** How many projection vectors are drawn. More than this and the chart stops being readable. */
export const MAX_DRAWN_PROJECTIONS = 4;

/**
 * Each standing projection as a vector the chart can draw: where it starts, where it says price
 * should get to, and how long it says that should take.
 *
 * <h2>The length in time is the ratio applied to the measured leg</h2>
 * A wave projected at 1.618x of wave 1 in PRICE is drawn 1.618x of wave 1 in BARS. That is the
 * plain reading of the guideline rather than a second rule, and it is what makes the drawn segment
 * a direction and a length rather than a horizontal level with a price on it. It is capped at the
 * horizon the projection is JUDGED over, so the picture and the verdict cannot disagree about the
 * window in question.
 *
 * <h2>Reached ones carry where price actually got there</h2>
 * `reachedAt` is the real bar, which is generally not the projected one — the difference between
 * the two is how early or late the guideline was, and it is the most useful thing on the drawing.
 *
 * @param {Array<object>} verified projections carrying verdicts, from `verifyProjections`
 * @param {{limit?: number}} [opts]
 */
export function projectionVectors(verified, { limit = MAX_DRAWN_PROJECTIONS } = {}) {
  return (Array.isArray(verified) ? verified : [])
    .filter(p => p && Number.isInteger(p.baseIndex) && isNum(p.basePrice) && isNum(p.price))
    .slice(0, limit)
    .map((p) => {
      const legBars = Math.abs(Number(p.legBars) || 0);
      const horizon = Number.isFinite(p.horizonBars) ? p.horizonBars : Math.max(20, legBars * 3);
      const expectedBars = Math.max(1, Math.min(horizon, Math.round(legBars * p.ratio) || 1));
      const verdict = p.verdict || 'UNTESTABLE';
      const pctMove = p.basePrice !== 0 ? (p.price - p.basePrice) / Math.abs(p.basePrice) : null;
      const sign = p.price >= p.basePrice ? '+' : '';

      return {
        wave: p.wave,
        ratio: p.ratio,
        verdict,
        // ONE pen for every projection, so a projection is recognisable as one before it is read.
        // Settled ones are the same hue a shade darker - still plainly a projection, visibly no
        // longer live - and the verdict's own colour goes on the badge below.
        color: verdict === 'UNTESTABLE' || verdict === 'OPEN'
          ? PROJECTION_COLOR : PROJECTION_RESOLVED_COLOR,
        badgeColor: VERDICT_COLORS[verdict] || VERDICT_COLORS.UNTESTABLE,
        from: { index: p.baseIndex, price: p.basePrice },
        to: { index: p.baseIndex + expectedBars, price: p.price },
        expectedBars,
        // Where price actually reached it, when it did. Null otherwise - NOT the projected bar,
        // which would draw a result that did not happen.
        reachedAt: verdict === 'REACHED' && Number.isInteger(p.reachedAtIndex)
          ? { index: p.reachedAtIndex, price: p.price } : null,
        up: p.price >= p.basePrice,
        // Three lines: what it is, how far and how long, and what became of it.
        label: [
          `W${p.wave} ${p.ratio === 1 ? 'equality' : `${p.ratio}x`} -> ${p.price.toFixed(2)}`,
          `${sign}${(p.price - p.basePrice).toFixed(2)}`
            + `${pctMove === null ? '' : ` (${sign}${(pctMove * 100).toFixed(1)}%)`}`
            + ` over ~${expectedBars} bars`,
          (VERDICT_TEXT[verdict] || VERDICT_TEXT.UNTESTABLE)(p),
        ].join('\n'),
      };
    });
}
